import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { JobPosition, Application, CompanyTeam, AiChatContext, AiConversationState } from '@/types';
import { useAiChatStore, isSessionExpired, EMPTY_SESSION } from '@/stores/aiChatStore';
import { useGlobalLoading } from '@/stores/loadingStore';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import { buildApplicationsContext, type ActiveFilters as SharedActiveFilters } from '@/lib/aiContext';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import {
  useCompanyTeamsQuery,
  usePositionsQuery,
} from '@/hooks/useRecruiterListQueries';

export interface UseApplicationsPageDataReturn {
  positions: JobPosition[];
  companyTeams: CompanyTeam[];
  loading: boolean;
  error: string | null;
  refetchApplications: () => Promise<void>;
  patchApplications: (updates: Application[] | ((prev: Application[]) => Application[])) => void;
  appendManualApplication: (positionId: string, applicationId: string) => Promise<void>;
  // AI Chat
  chatOpen: boolean;
  setChatOpen: (v: boolean) => void;
  chatMessages: { role: 'user' | 'ai'; text: string }[];
  chatInput: string;
  setChatInput: (v: string) => void;
  chatLoading: boolean;
  chatStreamStatuses: string[];
  chatExpanded: boolean;
  setChatExpanded: (v: boolean) => void;
  sendChat: (
    overrideMessage?: string,
    currentFilters?: ActiveFilters,
    applicationsForContext?: Application[],
  ) => Promise<void>;
  resetChat: () => void;
  conversationState: AiConversationState | null;
}

// ActiveFilters re-exported from the shared aiContext lib for backward
// compatibility (existing consumers still import it from this module).
export type ActiveFilters = SharedActiveFilters;

// buildApplicationsContext moved to @/lib/aiContext.ts so that both the
// /applications page and the PositionDetailPage chat can reuse it.
// Sprint ai_assistant_scope_fix (26/05/26).

function firstQueryError(...errors: unknown[]): string | null {
  for (const err of errors) {
    if (!err) continue;
    return err instanceof Error ? err.message : 'Failed to load applications';
  }
  return null;
}

export function useApplicationsPageData(companyId: string | undefined): UseApplicationsPageDataReturn {
  const { selectedCompany, user } = useAuth();
  const { t } = useLanguage();
  const listSync = useApplicationListSync();

  const positionsQuery = usePositionsQuery(companyId);
  const companyTeamsQuery = useCompanyTeamsQuery(companyId);

  const positions = positionsQuery.data ?? [];
  const companyTeams = companyTeamsQuery.data ?? [];
  const loading = !!companyId && (
    positionsQuery.isLoading
    || companyTeamsQuery.isLoading
  );
  const error = firstQueryError(
    positionsQuery.error,
    companyTeamsQuery.error,
  );

  useGlobalLoading(loading, 'applications');

  // AI Chat state (transient)
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreamStatuses, setChatStreamStatuses] = useState<string[]>([]);
  const [chatStreamingText, setChatStreamingText] = useState('');
  const chatGenRef = useRef(0);
  const chatAbortRef = useRef<AbortController | null>(null);

  // Persistent chat session keyed by companyId
  const sessionKey = companyId ? `applications_${companyId}` : null;
  const setSession = useAiChatStore(s => s.setSession);
  const clearSession = useAiChatStore(s => s.clearSession);

  // Direct reactive selector — re-renders when store hydrates from localStorage on refresh
  const rawSession = useAiChatStore(s => sessionKey ? s.sessions[sessionKey] ?? null : null);
  const chatSession = rawSession && !isSessionExpired(rawSession.lastInteractionAt) ? rawSession : null;
  const chatMessages = chatSession?.messages ?? [];
  const renderedChatMessages = chatStreamingText
    ? chatMessages.map((msg, index) => (
        index === chatMessages.length - 1 && msg.role === 'ai'
          ? { ...msg, text: chatStreamingText }
          : msg
      ))
    : chatMessages;
  const conversationState = chatSession?.conversationState ?? null;
  const chatOpen = chatSession?.isOpen ?? false;
  const chatExpanded = chatSession?.isExpanded ?? false;

  const setChatOpen = useCallback((v: boolean) => {
    // Fix 1: guard against null sessionKey (e.g. companyId not yet resolved from Auth)
    if (!sessionKey) {
      if (import.meta.env.DEV) console.warn('[useApplicationsPageData] setChatOpen called before sessionKey is ready');
      return;
    }
    // Fix 2: if opening and the stored session is expired (or missing), initialize a
    // fresh session so the reactive selector picks it up correctly instead of silently
    // writing isOpen onto a stale entry that gets discarded on read.
    if (v && (!rawSession || isSessionExpired(rawSession.lastInteractionAt))) {
      setSession(sessionKey, { ...EMPTY_SESSION, isOpen: true });
    } else {
      setSession(sessionKey, { isOpen: v });
    }
  }, [sessionKey, rawSession, setSession]);

  const setChatExpanded = useCallback((v: boolean) => {
    if (!sessionKey) return;
    setSession(sessionKey, { isExpanded: v });
  }, [sessionKey, setSession]);

  useEffect(() => {
    if (!chatOpen) setChatInput('');
  }, [chatOpen]);

  const refetchApplications = useCallback(async () => {
    if (!companyId) return;
    const cached = listSync.getFlattenedListApplications(companyId, false);
    if (cached.length === 0) {
      await listSync.refetchAllCompanyScopes(companyId);
    }
  }, [companyId, listSync]);

  const patchApplications = useCallback((
    updates: Application[] | ((prev: Application[]) => Application[]),
  ) => {
    if (!companyId) return;
    if (typeof updates === 'function') {
      listSync.patchCompanyUpdater(companyId, updates, false);
      return;
    }
    listSync.patchMany(companyId, updates, false);
  }, [companyId, listSync]);

  const appendManualApplication = useCallback(async (
    positionId: string,
    applicationId: string,
  ) => {
    if (!companyId) return;
    await listSync.upsertOne(companyId, positionId, applicationId);
  }, [companyId, listSync]);

  // Sprint 2 (S2.E) — refetch applications when the plan changes. Newly
  // upgraded users have PENDING_UPGRADE rows that the BE will reprocess once
  // the recruiter clicks "Process all pending"; refreshing the list here
  // makes sure the badge counts and CTA visibility line up with the new
  // entitlements. We import lazily inside the effect so server bundles don't
  // pull window APIs at import time.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    void import('@/lib/billingEvents').then(({ onPlanChanged }) => {
      if (cancelled) return;
      unsubscribe = onPlanChanged(() => {
        void refetchApplications();
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [companyId, refetchApplications]);

  // Clear input only when chat is closed — messages/state persist in store
  useEffect(() => {
    if (!chatOpen) setChatInput('');
  }, [chatOpen]);

  /**
   * sendChat — sends a message to the applications_assistant.
   *
   * First turn: builds a full pipeline snapshot (buildApplicationsContext) and sends it
   * as context — zero extra API calls, all data from FE memory.
   * Subsequent turns: sends only companyId (already in conversation history).
   *
   * @param overrideMessage - optional message to send instead of chatInput (for suggested prompts)
   * @param currentFilters - current active filters from the page (for snapshot accuracy)
   */
  const sendChat = useCallback(async (
    overrideMessage?: string,
    currentFilters: ActiveFilters = {},
    applicationsForContext: Application[] = [],
  ) => {
    const message = (overrideMessage ?? chatInput).trim();
    if (!message || !selectedCompany) return;
    if (!sessionKey) return;

    chatAbortRef.current?.abort();
    chatAbortRef.current = new AbortController();

    const gen = ++chatGenRef.current;
    const now = new Date().toISOString();

    const applicationsContext = buildApplicationsContext(
      selectedCompany.id,
      applicationsForContext,
      positions,
      currentFilters,
      undefined,
      undefined,
      user?.id,
    );
    const context: AiChatContext = { applicationsContext };

    // Append user message + AI placeholder
    setSession(sessionKey, {
      messages: [...chatMessages, { role: 'user', text: message }, { role: 'ai', text: '' }],
      lastInteractionAt: now,
    });
    setChatInput('');
    setChatLoading(true);
    setChatStreamStatuses([]);
    setChatStreamingText('');

    let streamedText = '';

    try {
      await api.ai.chatStream(
        selectedCompany.id,
        {
          profileName: 'applications_assistant',
          message,
          conversationState: conversationState ?? undefined,
          context,
        },
        {
          onStatus: (msg) => {
            if (gen !== chatGenRef.current) return;
            setChatStreamStatuses(prev => prev[prev.length - 1] === msg ? prev : [...prev, msg]);
          },
          onChunk: (chunk) => {
            if (gen !== chatGenRef.current) return;
            streamedText += chunk;
            setChatStreamingText(streamedText);
          },
          onFinal: (response) => {
            if (gen !== chatGenRef.current) return;
            setSession(sessionKey, {
              messages: [...chatMessages, { role: 'user', text: message }, { role: 'ai', text: response.text }],
              conversationState: response.conversationState,
              lastInteractionAt: new Date().toISOString(),
            });
            setChatStreamStatuses([]);
            setChatStreamingText('');
            setChatLoading(false);
            emitBillingUsageChanged();
          },
          onError: (errMsg) => {
            if (gen !== chatGenRef.current) return;
            const errorText = errMsg.includes('not authorized') || errMsg.includes('402')
              ? t('billing.aiNotEnabled')
              : 'Something went wrong. Please try again.';
            setSession(sessionKey, {
              messages: [...chatMessages, { role: 'user', text: message }, { role: 'ai', text: errorText }],
              lastInteractionAt: new Date().toISOString(),
            });
            setChatStreamStatuses([]);
            setChatStreamingText('');
            setChatLoading(false);
          },
        },
        chatAbortRef.current.signal,
      );
    } catch (err: unknown) {
      if (gen !== chatGenRef.current) return;
      const apiErr = err as { status?: number; message?: string };
      const errorText = apiErr?.status === 402
        ? t('billing.aiNotEnabled')
        : (apiErr?.message ?? '').includes('demo')
        ? 'AI features are not available in demo mode.'
        : 'Something went wrong. Please try again.';
      setSession(sessionKey, {
        messages: [...chatMessages, { role: 'user', text: message }, { role: 'ai', text: errorText }],
        lastInteractionAt: new Date().toISOString(),
      });
      setChatStreamStatuses([]);
      setChatStreamingText('');
      setChatLoading(false);
    }
  }, [chatInput, selectedCompany, positions, conversationState, sessionKey, chatMessages, setSession, t, user?.id]);

  return {
    positions,
    companyTeams,
    loading,
    error,
    refetchApplications,
    patchApplications,
    appendManualApplication,
    chatOpen,
    setChatOpen,
    chatMessages: renderedChatMessages,
    chatInput,
    setChatInput,
    chatLoading,
    chatStreamStatuses,
    chatExpanded,
    setChatExpanded,
    sendChat,
    resetChat: useCallback(() => {
      chatAbortRef.current?.abort();
      chatGenRef.current++;
      setChatLoading(false);
      setChatStreamStatuses([]);
      setChatStreamingText('');
      if (sessionKey) setSession(sessionKey, { messages: [], conversationState: null, lastInteractionAt: null });
      setChatInput('');
    }, [sessionKey, setSession]),
    conversationState,
  };
}
